import { BoxHelper, Group, Matrix4, Vector3 } from 'three';
import type { Box3, LineBasicMaterial } from 'three';

import type {
  CompositeProgressController,
  AnyProgressSignal,
} from '@frontend/controllers';
import {
  WaitUntilSetController,
  addEventListenerAndCollectRemovers,
  runRemoveEventListeners,
} from '@frontend/utils';

import {
  type PART_TYPES,
  ENTITY_TYPES,
  ActiveRepresentation,
} from '../constants';
import type { AssemblyInstanceJson } from '../contracts/assemblyInstance';
import {
  getMidpoint,
  isIdentityTransformArray,
  identityTransform,
  disposeThreeJS,
} from '../geom-utils';
import { disposeAdditionalFeedback } from '../renderer/creation-utils';
import type {
  AsyncLoadable,
  SyncLoadable,
} from '../renderer/loadingPriorityQueue';
import type { HasRenderable, Renderer } from '../renderer/renderer';
import { viewerDataService } from '../viewerDataService';
import { OBBSimplifiedRepresentation } from './OBBSimplifiedRepresentation';
import { OptimalBoundingBox } from './OptimalBoundingBox';
import type { Assembly } from './assembly';
import { Body } from './body';
import type { AnyBody } from './body';
import { constructEntityName } from './step/utils';
import { compareByOBBMaxSideArea, type HasMaxSideArea } from './utils';

export class AssemblyInstance
  implements AsyncLoadable, SyncLoadable, HasRenderable, HasMaxSideArea
{
  public readonly id?: string;
  public readonly name: string;
  public readonly partUuid?: string;
  public readonly isRoot?: boolean;
  public readonly childAssemblyInstances: AssemblyInstance[] = [];
  public readonly childBodies: Body<PART_TYPES.STEP | PART_TYPES.DXF>[] = []; //STL doesn't have children
  public readonly fullDetailRep: Group = new Group();
  public readonly group: Group = new Group();
  public readonly optimalBoundingBox?: OptimalBoundingBox;

  public readonly additionalFeedback: Record<string, Group> = {};
  public readonly additionalFeedbackGroup: Group = new Group();
  public readonly initialPosition: Vector3 = new Vector3();
  public readonly initialBoxMidpoint: Vector3 = new Vector3(); //used for explode utils

  private _objectIds?: string[]; //only used to establish assembly structure (not needed after loaded)
  private _sortedChildren?: (
    | AssemblyInstance
    | Body<PART_TYPES.STEP | PART_TYPES.DXF>
  )[];

  private _obbSimplifiedRep?: OBBSimplifiedRepresentation;
  private _parent?: AssemblyInstance;
  private _activeRepresentation: ActiveRepresentation =
    ActiveRepresentation.FullDetailRep;
  private _boundBox?: BoxHelper; //set by updateBoundBox()
  private _transform?: Matrix4; //only defined if NOT identityTransform
  private resumeController?: WaitUntilSetController<void>;
  private loaded?: Promise<number>; //presence indicates this.isLoading. Resolves when loaded
  private _progress?: CompositeProgressController;
  private _isLoaded?: boolean;
  private _geometryVerticesCount?: number;
  private disposeFns: (() => void)[] = [];
  private isDisposed?: boolean;

  constructor(
    data: AssemblyInstanceJson,
    public readonly assembly: Assembly,
    private renderer: Renderer,
    parentProgress?: CompositeProgressController,
    private readonly signal?: AbortSignal, //AbortSignal to abort loading
    public readonly isMaterial = false,
  ) {
    this.id = data.id;
    this.name = data.name;
    this._objectIds =
      data.objectIds || data.object_ids || data.childViewerIds || [];
    this.group.name = `asmInst|${this.id}`;
    this.additionalFeedbackGroup.name = `asmInst|${this.id}|additionalFeedback`;
    this.partUuid = data.partFileUuid || data.part_uuid; //pcad provides part_uuid
    if (data.transform && !isIdentityTransformArray(data.transform)) {
      this._transform = new Matrix4().fromArray(data.transform); //only store if not identity
      //Note: There are a few cases where assembly has transform... but when it does, we don't use it???
      //It seems the convention is to always assume the descendent bodies have the parent transforms incorporated
      //this.group.applyMatrix4(this._transform);
    }
    this.initialPosition = this.group.position.clone();

    this.isRoot = data.isRoot || data.is_root; //pcad provides is_root

    this.fullDetailRep.name = `asmInstFullDetailRep|${this.id}`;
    this.group.add(this.fullDetailRep);

    //Establish optimalBoundingBox
    if (this.assembly.optimalBoundingBoxGeometry) {
      this.optimalBoundingBox = new OptimalBoundingBox(
        this.assembly.optimalBoundingBoxGeometry,
      );
      this.optimalBoundingBox.group.applyMatrix4(this.transform);
    }

    this.establishAdditionalFeedback();

    this._progress = parentProgress?.pushCompositeProgress({
      // * Note: RendererState.visibleBodies includes both bodies and assembly instances
      // * And RendererState.loadingProgress is the composite progress controller for many constiuent progress signals
      //   * The composite progress controller only reports on contiuents that are visible
      // * In this.state.loadingProgress.appendVisibleIds(), visibleBodies includes both body and assemblyInstance ids. So it is convenient to use
      //   the same suffix for both bodies and assemblyInstances.  This is why `Body.suffix` is used here.
      id: viewerDataService.getBodyOrAssemblyInstanceLoadingId(
        this.id as string,
      ),
      description: `Loading Assembly Instance for ${
        assembly.name || assembly.id
      } (instance ${this.id})`,
    });
    if (signal) {
      addEventListenerAndCollectRemovers(
        this.disposeFns,
        signal,
        'abort',
        () => {
          this.dispose();
        },
      );
    }
  }

  public dispose() {
    if (this.isDisposed) return;
    this.isDisposed = true;
    runRemoveEventListeners(this.disposeFns);
    this.abortLoad();
    for (const cs of [this.childAssemblyInstances, this.childBodies]) {
      for (const c of cs) {
        c.dispose();
      }
      cs.length = 0;
    }
    delete this._transform;
    delete this._parent;
    if (this._sortedChildren) {
      this._sortedChildren.length = 0;
      delete this._sortedChildren;
    }
    if (this._obbSimplifiedRep) {
      this._obbSimplifiedRep.dispose();
    }
    if (this.optimalBoundingBox) {
      this.optimalBoundingBox.dispose();
    }
    disposeThreeJS(this._boundBox, this.fullDetailRep, this.group);

    //Note: additionalFeedback was cloned from assembly's additionalFeedback (establishAdditionalFeedback)
    //So it needs to be cleaned
    disposeAdditionalFeedback(this.additionalFeedback);
    this.additionalFeedbackGroup.clear();
    this.additionalFeedbackGroup.removeFromParent();
  }

  //Note: objectIds is only used by viewerDataService when loading
  //Later this.addChild() may be called
  public get objectIds(): Iterable<string> {
    //eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    return {
      *[Symbol.iterator]() {
        //Using Set because some consumers of this iterator may add children and don't want to yield duplicates
        const ids = new Set(self._objectIds ?? []);
        yield* ids;
        for (const { id } of self.children()) {
          if (typeof id === 'string' && !ids.has(id)) {
            ids.add(id);
            yield id;
          }
        }
      },
    };
  }

  public get renderable() {
    return this.group;
  }

  public get activeRepresentation(): ActiveRepresentation {
    return this._activeRepresentation;
  }

  public set activeRepresentation(v: ActiveRepresentation) {
    if (
      v === ActiveRepresentation.OBBSimplifiedRep &&
      this.optimalBoundingBox
    ) {
      if (!this._obbSimplifiedRep) {
        this.createObbSimpRep();
      }
      if (this._obbSimplifiedRep) {
        this._obbSimplifiedRep.visible = true;
        this.fullDetailRep.visible = false;
        this._activeRepresentation = v;
        this.renderer.intersectionEntities.add(
          this._obbSimplifiedRep.intersectable,
        );
      }
    } else {
      this.fullDetailRep.visible = true;
      if (this._obbSimplifiedRep) {
        this._obbSimplifiedRep.visible = false;
        this.renderer.intersectionEntities.delete(
          this._obbSimplifiedRep.intersectable,
        );
      }
      this._activeRepresentation = ActiveRepresentation.FullDetailRep;
    }
  }

  public toggleActiveRepresentation() {
    if (this._activeRepresentation === ActiveRepresentation.OBBSimplifiedRep) {
      this.activeRepresentation = ActiveRepresentation.FullDetailRep;
    } else if (
      this._activeRepresentation === ActiveRepresentation.FullDetailRep
    ) {
      this.activeRepresentation = ActiveRepresentation.OBBSimplifiedRep;
    }
  }

  public get obbMaxSideArea(): number {
    if (this.assembly.optimalBoundingBoxGeometry) {
      return this.assembly.optimalBoundingBoxGeometry.maxSideArea;
    }
    //Otherwise use axis aligned box if it exists
    if (this._boundBox) {
      const box = this._boundBox.geometry.boundingBox as Box3;
      const { x: width, y: length, z: height } = box.getSize(new Vector3());
      return [length * width, width * height, length * height]
        .map((v) => {
          const p = Math.abs(v);
          return Number.isFinite(p) ? p : 0;
        })
        .sort()[2];
    }
    return 0;
  }

  public get geometryVerticesCount(): number {
    if (this._geometryVerticesCount == null) {
      //calculate and memoize result
      let count = 0;
      for (const d of this.descendentBodies()) {
        count += d.geometryVerticesCount;
      }
      this._geometryVerticesCount = count;
    }
    return this._geometryVerticesCount;
  }

  public get transform(): Matrix4 {
    return this._transform || identityTransform;
  }

  public get parent(): AssemblyInstance | undefined {
    return this._parent;
  }

  public set parent(parent: AssemblyInstance | undefined) {
    if (parent !== this._parent) {
      if (this._parent) {
        throw new Error('Parent already set');
      }
      if (parent) {
        this._parent = parent;
      }
    }
  }

  public get boundBox(): BoxHelper {
    if (!this._boundBox) {
      throw new Error('AssemblyInstance has not been loaded');
    }
    return this._boundBox;
  }

  public get isLoaded() {
    //true after this.finalize() has finished
    return !!this._isLoaded;
  }

  public get isLoading(): boolean {
    return !!this.loaded;
  }

  public get progress(): AnyProgressSignal | undefined {
    return this._progress?.signal;
  }

  public async finishedAddingChildren(
    lastPause: number,
    parentProgress?: CompositeProgressController,
  ) {
    this._progress?.close();
    if (parentProgress) {
      //Important: loadAsyncUnhydrated so that the progress estimates are re-updated
      //This requires loading child parts so the body progress estimates can be updated, which in turn updates
      //the assembly instances estimates.  But we don't always want to load parts if they aren't going to be viewed
      //eslint-disable-next-line no-param-reassign
      lastPause = await this.loadAsyncUnhydrated(lastPause, parentProgress);
    }
    return lastPause;
  }

  public hasChild(
    childBodyOrAssemblyInstance?: AnyBody | AssemblyInstance,
  ): boolean {
    if (childBodyOrAssemblyInstance) {
      return (
        this.childBodies.includes(childBodyOrAssemblyInstance as AnyBody) ||
        this.childAssemblyInstances.includes(
          childBodyOrAssemblyInstance as AssemblyInstance,
        )
      );
    }
    return false;
  }

  public addChild(childBodyOrAssemblyInstance?: AnyBody | AssemblyInstance) {
    if (childBodyOrAssemblyInstance) {
      if (childBodyOrAssemblyInstance instanceof Body) {
        if (!this.childBodies.includes(childBodyOrAssemblyInstance)) {
          this.childBodies.push(childBodyOrAssemblyInstance);
        }
      } else {
        if (
          !this.childAssemblyInstances.includes(childBodyOrAssemblyInstance)
        ) {
          this.childAssemblyInstances.push(childBodyOrAssemblyInstance);
        }
      }
      this.updateSortedChildren();
      if (!childBodyOrAssemblyInstance.parent) {
        childBodyOrAssemblyInstance.parent = this;
        this.fullDetailRep.add(childBodyOrAssemblyInstance.group);
      }
      //Update boundBox of self and ancestors
      //eslint-disable-next-line @typescript-eslint/no-this-alias
      let current: AssemblyInstance | undefined = this;
      while (current) {
        current.updateBoundBox();
        current = current._parent;
      }
      if (this._progress && !this._progress.isClosed) {
        this._progress.push({ signal: childBodyOrAssemblyInstance.progress });
      }
    }
  }

  public removeChild(
    childBodyOrAssemblyInstance?: AnyBody | AssemblyInstance,
  ): boolean {
    let removed = false;
    if (childBodyOrAssemblyInstance) {
      if (childBodyOrAssemblyInstance instanceof Body) {
        const objectIdIndex = this.childBodies.findIndex(
          (_) => (childBodyOrAssemblyInstance.id as string) === _.id,
        );
        if (objectIdIndex > -1) {
          this.childBodies.splice(objectIdIndex, 1); //remove it
          removed = true;
        }
      } else {
        const objectIdIndex = this.childAssemblyInstances.findIndex(
          (_) => (childBodyOrAssemblyInstance.id as string) === _.id,
        );
        if (objectIdIndex > -1) {
          this.childAssemblyInstances.splice(objectIdIndex, 1); //remove it
          removed = true;
        }
      }
      if (removed) {
        this.fullDetailRep.remove(childBodyOrAssemblyInstance.group);
        if (childBodyOrAssemblyInstance.parent === this) {
          delete childBodyOrAssemblyInstance.parent;
        }
        //Update boundBox of self and ancestors
        //eslint-disable-next-line @typescript-eslint/no-this-alias
        let current: AssemblyInstance | undefined = this;
        while (current) {
          current.updateBoundBox();
          current = current._parent;
        }
        if (this._progress && !this._progress.isClosed) {
          this._progress.remove({
            signal: childBodyOrAssemblyInstance.progress,
          });
        }
      }
    }
    return removed;
  }

  public *descendentBodies(): IterableIterator<AnyBody> {
    for (const child of this.childAssemblyInstances) {
      yield* child.descendentBodies();
    }
    yield* this.childBodies;
  }

  public *loadingIds(): IterableIterator<string> {
    yield viewerDataService.getBodyOrAssemblyInstanceLoadingId(
      this.id as string,
    );
    for (const child of this.childAssemblyInstances) {
      yield* child.loadingIds();
    }
    for (const child of this.childBodies) {
      yield viewerDataService.getBodyOrAssemblyInstanceLoadingId(
        child.id as string,
      );
    }
  }

  public *children(): IterableIterator<AnyBody | AssemblyInstance> {
    yield* this.childAssemblyInstances;
    yield* this.childBodies;
  }

  public pause() {
    if (!this.resumeController && !this.signal?.aborted) {
      this.resumeController = new WaitUntilSetController(this.signal);
    }
  }

  public get isPaused(): boolean {
    return !!this.resumeController;
  }

  public resume() {
    if (this.resumeController && !this.signal?.aborted) {
      this.resumeController.set();
      delete this.resumeController;
    }
  }

  public abortLoad() {
    if (this.loaded) {
      this.pause(); //force pause so we can reject
      this.loaded.catch(() => Promise.resolve()); //the promise's rejection should be handled at least once
      delete this.loaded;
    }
    if (this.resumeController) {
      this.resumeController.setReject();
      delete this.resumeController;
    }
    if (this._progress) {
      this._progress.close();
      delete this._progress;
    }
  }

  public get hasAssemblyStructureBeenEstablished(): boolean {
    //If !root, then a parent should be defined
    //Note this is a better test than `this.childBodies.length === 0 && this.childAssemblyInstances.length === 0`
    //* there are edge cases where an assemblyInstance could have no bodies
    return !!(this.isRoot || this._parent);
  }

  public get rootPartFileUuid(): string {
    if (!this.hasAssemblyStructureBeenEstablished) {
      throw new Error('Assembly structure has not been established.');
    }
    //eslint-disable-next-line @typescript-eslint/no-this-alias
    let node: AssemblyInstance | Body<PART_TYPES.STEP | PART_TYPES.DXF> = this;
    while (node.parent) {
      node = node.parent;
    }
    const partOrAssembly =
      node &&
      ((node as unknown as Body<PART_TYPES.STEP | PART_TYPES.DXF>).part ||
        (node as unknown as AssemblyInstance).assembly);
    return partOrAssembly?.id || '';
  }

  public get rootBodyOrAssemblyInstanceId(): string {
    if (!this.hasAssemblyStructureBeenEstablished) {
      throw new Error('Assembly structure has not been established.');
    }
    //eslint-disable-next-line @typescript-eslint/no-this-alias
    let node: AssemblyInstance | AnyBody = this;
    while (node.parent) {
      node = node.parent;
    }
    return node?.id || '';
  }

  public get obbSimplifiedRep() {
    return this._obbSimplifiedRep;
  }

  public load(): void {
    if (this._isLoaded || this.signal?.aborted) {
      return;
    }
    if (this.isLoading) {
      throw new Error(
        'Cannot load assemblyInstance synchronously that already started async loading',
      );
    }
    if (!this.hasAssemblyStructureBeenEstablished) {
      throw new Error('Assembly structure has not been established.');
    }

    for (const child of [
      ...this.childBodies,
      ...this.childAssemblyInstances,
    ].sort(compareByOBBMaxSideArea)) {
      if (!child.isLoaded) {
        child.load();
      }
    }
    this.finalize();
    this.resume(); //cleanup resumeController if it exists
  }

  public async loadAsyncUnhydrated(
    lastPause: number,
    parentProgress?: CompositeProgressController,
  ): Promise<number> {
    if (!this._isLoaded && !this.signal?.aborted) {
      const sortedChildren =
        this._sortedChildren ?? this.updateSortedChildren();
      for (const child of sortedChildren) {
        if (!child.isLoaded && !this.signal?.aborted) {
          //eslint-disable-next-line no-param-reassign
          lastPause = await child.loadAsyncUnhydrated(
            lastPause,
            parentProgress,
          );
          if (this.resumeController) {
            await this.resumeController.waitUntilSet;
            //eslint-disable-next-line no-param-reassign
            lastPause = performance.now();
          }
        }
      }
    }
    return lastPause;
  }

  public async loadAsync(
    lastPause: number = performance.now(),
  ): Promise<number> {
    if (this._isLoaded || this.signal?.aborted) {
      return lastPause;
    } else if (!this.loaded) {
      const finalize = this.finalize.bind(this);
      if (!this.hasAssemblyStructureBeenEstablished) {
        throw new Error('Assembly structure has not been established.');
      }
      this.loaded = (async () => {
        //Load unhydrated bodies first so it updates the todo count sooner
        const sortedChildren =
          this._sortedChildren ?? this.updateSortedChildren();
        for (const child of sortedChildren) {
          //eslint-disable-next-line no-param-reassign
          lastPause = await child.loadAsyncUnhydrated(lastPause);
        }
        for (const child of sortedChildren) {
          if (!child.isLoaded && !this.signal?.aborted) {
            //eslint-disable-next-line no-param-reassign
            lastPause = await child.loadAsync(lastPause);
            if (this.resumeController) {
              await this.resumeController.waitUntilSet;
              //eslint-disable-next-line no-param-reassign
              lastPause = performance.now();
            }
            if (this.signal?.aborted) {
              break;
            }
          }
        }
        finalize();
        this.resume(); //cleanup resumeController if it exists
        return lastPause;
      })();
    } else if (this.isPaused) {
      this.resume();
    }
    return this.loaded;
  }

  public getExplodeTranslationVector(
    parentBoxCenter: Vector3,
    factor: number,
  ): Vector3 {
    // based on parent bounding box center and our initial center, calculate the translation vector
    // for the body given zero state. Assumes the factor is scalar multiple of original center to center length
    return new Vector3()
      .subVectors(this.initialBoxMidpoint, parentBoxCenter)
      .multiplyScalar(factor);
  }

  public establishAdditionalFeedback(): void {
    for (const key in this.assembly.additionalFeedback) {
      if (key in this.additionalFeedback) continue;
      const newGroup = this.assembly.additionalFeedback[key].clone();
      newGroup.name = `asmInst|${this.id}|additionalFeedback|${key}`;
      this.additionalFeedback[key] = newGroup;
      this.additionalFeedbackGroup.add(newGroup);
    }
  }

  public getEntityInformation(): {
    minRenderCount: number;
    minInteractionCount: number;
    maxRenderCount: number;
    maxInteractionCount: number;
  } {
    let minRenderCount = 0;
    let maxRenderCount = 0;
    const minInteractionCount = 0;
    const maxInteractionCount = 0;
    if (this.additionalFeedbackGroup.visible) {
      for (const key in this.additionalFeedback) {
        const group = this.additionalFeedback[key];
        if (group.visible) {
          maxRenderCount += group.children.length;
          minRenderCount += group.children.length;
          for (const child of group.children) {
            maxRenderCount += child.children.length;
            minRenderCount += child.children.length;
          }
        }
      }
    }
    return {
      minRenderCount,
      minInteractionCount,
      maxRenderCount,
      maxInteractionCount,
    };
  }

  public updateBoundBox() {
    // make bound box, needs to be relative to group
    if (this._boundBox) {
      this._boundBox.copy(new BoxHelper(this.fullDetailRep));
      this._boundBox.geometry.boundingBox = null; //need to recompute
    } else {
      this._boundBox = new BoxHelper(this.fullDetailRep);
    }
    (this._boundBox.material as LineBasicMaterial).linewidth = 2;
    this._boundBox.visible = false;
    this._boundBox.geometry.computeBoundingBox();

    const box = this._boundBox.geometry.boundingBox as Box3;
    this.initialBoxMidpoint.copy(getMidpoint(box.min, box.max));
  }

  //obbSimplifiedRep is created on demand for performance reasons.
  //The vast majority of time we never need it... and when we do need it, there tends to be a lot of components so creating it slows things down
  private createObbSimpRep() {
    if (this.optimalBoundingBox && this.assembly.optimalBoundingBoxGeometry) {
      this._obbSimplifiedRep = new OBBSimplifiedRepresentation(
        this.assembly.optimalBoundingBoxGeometry,
        constructEntityName(
          this.id ?? 'undefined',
          ENTITY_TYPES.SimpRep,
          'obb',
        ),
        this,
      );
      if (!this._isLoaded) {
        //_boundBox is initially based on obbSimplifiedRepsooner but will be replaced
        this._boundBox = new BoxHelper(this._obbSimplifiedRep.intersectable);
        (this._boundBox.material as LineBasicMaterial).linewidth = 2;
        this._boundBox.visible = false;
        this._boundBox.geometry.computeBoundingBox();
        const box = this._boundBox.geometry.boundingBox as Box3;
        this.initialBoxMidpoint.copy(getMidpoint(box.min, box.max));
      }
    }
    return this._obbSimplifiedRep;
  }

  private updateSortedChildren() {
    return (this._sortedChildren = [
      ...this.childBodies,
      ...this.childAssemblyInstances,
    ].sort(compareByOBBMaxSideArea));
  }

  //Don't call finalize() until ALL children are loaded
  private finalize(): void {
    this.updateBoundBox();
    delete this._geometryVerticesCount; //remove memoized value just in case it was already calculated
    delete this._objectIds;
    delete this._sortedChildren;
    if (this._progress) {
      this._progress.close(); //should already be closed (but just in case)
      this._progress.disposeIfUnused();
      delete this._progress;
    }
    this._isLoaded = true;
  }
}
export default AssemblyInstance;
